// Dependencies
import { Component } from 'react';
import { connect } from 'react-redux';

import FormGroup from 'components/atoms/FormGroup';
import Input from 'components/atoms/Input';
import Label from 'components/atoms/Label';
import ToggleButton from 'components/atoms/ToggleButton';
import TypeTag from 'components/atoms/TypeTag';
import { SetSearchObjectAction } from 'store/actions/layoutActions';
import { OpenModal, RootState, TagType } from 'types';
import { DefaultData, FilterObjectType } from 'types/search';
import { checkDifferent } from 'utils/formHandlers';
import { translate } from 'utils/translate';
import { twclsx } from 'utils/twclsx';
import { toggleStringInArray } from 'utils/unit';
import { SystemType } from 'types/system';

interface TagsAndCommentsFiltersComponentProps {
    tags: TagType[];
    system: SystemType;
    defaultData: DefaultData;
    filterObject: FilterObjectType;
    openModelFunction: OpenModal;
    setFilterObject: SetSearchObjectAction;
}

interface TagsAndCommentsFiltersComponentState {
    language: string;
    errors: object;
    searchTags: TagType[];
    tags: TagType[];
    searchQuery: string;
    filterObject: FilterObjectType;
    withComments: boolean;
    selectedTags: number[];
    selectedType: string[];
}

class TagsAndCommentsFilters extends Component<
    TagsAndCommentsFiltersComponentProps,
    TagsAndCommentsFiltersComponentState
> {
    constructor(props: TagsAndCommentsFiltersComponentProps) {
        super(props);
        this.state = {
            filterObject: props.filterObject,
            withComments: props.filterObject?.withComments,
            selectedTags: props.filterObject?.tags,
            errors: {},
            language: 'en',
            searchQuery: '',
            tags: props.tags
                .filter((tag: TagType) => tag.type === 'private' || tag.type === 'public' || tag.type === 'analysis')
                .sort((a, b) => {
                    const aGroup = a.type === 'private' || a.type === 'public' ? 1 : 2;
                    const bGroup = b.type === 'private' || b.type === 'public' ? 1 : 2;

                    if (aGroup !== bGroup) {
                        return aGroup - bGroup;
                    }

                    if (a.order == null && b.order == null) return 0;
                    if (a.order == null) return 1;
                    if (b.order == null) return -1;

                    return a.order - b.order;
                }),

            searchTags: props.tags
                .filter((tag: TagType) => tag.type === 'private' || tag.type === 'public' || tag.type === 'analysis')
                .sort((a, b) => {
                    const aGroup = a.type === 'private' || a.type === 'public' ? 1 : 2;
                    const bGroup = b.type === 'private' || b.type === 'public' ? 1 : 2;

                    if (aGroup !== bGroup) {
                        return aGroup - bGroup;
                    }

                    if (a.order == null && b.order == null) return 0;
                    if (a.order == null) return 1;
                    if (b.order == null) return -1;

                    return a.order - b.order;
                }),

            selectedType: ['user', 'analysis', 'public', 'private'],
        };
    }

    static getDerivedStateFromProps(
        props: TagsAndCommentsFiltersComponentProps,
        state: TagsAndCommentsFiltersComponentState
    ) {
        if (checkDifferent(props.tags, state.tags) || checkDifferent(props.filterObject, state.filterObject)) {
            return { tags: props.tags, filterObject: props.filterObject, selectedTags: props.filterObject?.tags };
        }
        return null;
    }

    search = (name: string, value: string) => {
        this.setState({
            searchQuery: value,
            searchTags:
                value.length === 0
                    ? this.state.tags
                        .filter((tag: TagType) => {
                            return tag.type === 'private' || tag.type === 'public' || tag.type === 'analysis';
                        })
                        .filter((tag) => this.state.selectedType.includes(tag.type))
                        .sort((a, b) => {
                            const aGroup = a.type === 'private' || a.type === 'public' ? 1 : 2;
                            const bGroup = b.type === 'private' || b.type === 'public' ? 1 : 2;

                            if (aGroup !== bGroup) {
                                return aGroup - bGroup;
                            }

                            if (a.order == null && b.order == null) return 0;
                            if (a.order == null) return 1;
                            if (b.order == null) return -1;

                            return a.order - b.order;
                        })
                    : this.state.tags
                        .filter((tag: TagType) => {
                            return tag.label.toLocaleLowerCase().includes(value.toLocaleLowerCase());
                        })
                        .filter((tag) => this.state.selectedType.includes(tag.type))
                        .sort((a, b) => {
                            const aGroup = a.type === 'private' || a.type === 'public' ? 1 : 2;
                            const bGroup = b.type === 'private' || b.type === 'public' ? 1 : 2;

                            if (aGroup !== bGroup) {
                                return aGroup - bGroup;
                            }

                            if (a.order == null && b.order == null) return 0;
                            if (a.order == null) return 1;
                            if (b.order == null) return -1;

                            return a.order - b.order;
                        }),
        });
    };

    save = (selectedOrigins: string[], selectedTags: number[], withComments: boolean) => {
        const filterObject = {
            ...this.state.filterObject,
            withComments,
            tags: selectedTags,
            origin: selectedOrigins,
        };
        this.props.setFilterObject(filterObject);
    };

    setTag = (id: number) => {
        this.setState(
            (prevState) => {
                const { selectedTags } = prevState;
                const isTagSelected = selectedTags.includes(id);

                const updatedTags = isTagSelected
                    ? selectedTags.filter((tag) => tag !== id) // Remove tag
                    : [...selectedTags, id]; // Add tag

                return { selectedTags: updatedTags };
            },
            () => {
                this.save(this.state.filterObject.origin, this.state.selectedTags, this.state.withComments);
            }
        );
    };

    setOrigin = (origin: string) => {
        const selectedOrigins = this.state.filterObject.origin;
        const isOriginSelected = selectedOrigins.includes(origin);

        const updatedOrigins = isOriginSelected
            ? selectedOrigins.filter((x) => x !== origin) // Remove tag
            : [...selectedOrigins, origin]; // Add tag

        this.save(updatedOrigins, this.state.selectedTags, this.state.withComments);
    };

    setWithComments = (name: string, value: boolean) => {
        this.setState(
            {
                withComments: value,
            },
            () => {
                this.save(this.state.filterObject.origin, this.state.selectedTags, this.state.withComments);
            }
        );
    };

    updateTags = (type: string[]) => {
        this.setState({ selectedType: toggleStringInArray(this.state.selectedType, type) }, () => {
            this.search('type', this.state.searchQuery);
        });
    };

    render() {
        return (
            <div
                //'border-b','border-gray-300'=> removed classes
                className={twclsx(
                    'flex',
                    'flex-col',
                    'pb-1',
                    'mt-1',
                    'px-2',
                    this.state.tags
                        ?.filter((tag: TagType) => {
                            return tag.type === 'private' || tag.type === 'public';
                        })
                        ?.filter((tag: TagType) => {
                            return this.state.filterObject?.tags.includes(tag.id);
                        }).length >= 1 && 'pb-2'
                )}
            >
                {/* <div className="flex w-full mb-2 justify-start gap-2">
                    <div>
                        <Icon>
                            <path d="M7.5 7.5m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                            <path d="M3 6v5.172a2 2 0 0 0 .586 1.414l7.71 7.71a2.41 2.41 0 0 0 3.408 0l5.592 -5.592a2.41 2.41 0 0 0 0 -3.408l-7.71 -7.71a2 2 0 0 0 -1.414 -.586h-5.172a3 3 0 0 0 -3 3z"></path>
                        </Icon>
                    </div>
                    <div onClick={this.selectTagsAndComments} className="font-bold no-select cursor-pointer  flex-none">
                        {translate('Tags and comments')}
                    </div>
                </div> */}
                {this.props.system.transits.simulated + this.props.system.transits.ppu >= 1 && (
                    <div className="mb-2">
                        <FormGroup>
                            <Label className="normal-case">{translate('filter by data source')}</Label>
                            <div className="flex gap-2">
                                <div
                                    onClick={() => {
                                        this.setOrigin('AIS');
                                    }}
                                >
                                    <TypeTag type="analysis" active={this.state.filterObject.origin.includes('AIS')}>
                                        {translate('AIS')}
                                    </TypeTag>
                                </div>

                                {this.props.system.transits.simulated >= 1 && (
                                    <div
                                        onClick={() => {
                                            this.setOrigin('SIM');
                                        }}
                                    >
                                        <TypeTag type="system" active={this.state.filterObject.origin.includes('SIM')}>
                                            {translate('simulated')}
                                        </TypeTag>
                                    </div>
                                )}
                                {this.props.system.transits.ppu >= 1 && (
                                    <div
                                        onClick={() => {
                                            this.setOrigin('PPU');
                                        }}
                                    >
                                        <TypeTag type="system" active={this.state.filterObject.origin.includes('PPU')}>
                                            {translate('ppu')}
                                        </TypeTag>
                                    </div>
                                )}
                            </div>
                        </FormGroup>
                    </div>
                )}
                <div className="mt-2 pr-2">
                    <div>
                        <Input
                            onChange={this.search}
                            placeholder={translate('Enter tag name')}
                            name="search"
                            className="px-4 py-1 rounded-md text-md text-bold  border border-gray-300"
                        ></Input>
                    </div>
                    <div className="mt-1 ">{translate('Select the tags to search for.')}</div>
                </div>
                <FormGroup>
                    <Label className="normal-case">{translate('filter the tags to display')}</Label>
                    <div className="flex gap-2">
                        <div
                            onClick={() => {
                                this.updateTags(['user', 'public', 'private']);
                            }}
                        >
                            <TypeTag type="user" active={this.state.selectedType.includes('user')}>
                                {translate('User')}
                            </TypeTag>
                        </div>
                        <div
                            onClick={() => {
                                this.updateTags(['analysis']);
                            }}
                        >
                            <TypeTag type="analysis" active={this.state.selectedType.includes('analysis')}>
                                {translate('Analysis')}
                            </TypeTag>
                        </div>
                    </div>
                </FormGroup>
                <div className=" w-full gap-2 mt-2">
                    {this.state.searchTags.map((tag: TagType) => {
                        return (
                            <TypeTag
                                className="mt-1 mr-1"
                                active={this.state.selectedTags.includes(tag.id)}
                                key={tag.id}
                                type={tag.type}
                                onClick={() => {
                                    this.setTag(tag.id);
                                }}
                            >
                                {tag.label}
                            </TypeTag>
                        );
                    })}
                </div>

                <div className="mt-2 flex items-center gap-1">
                    <div className="flex items-center align-middle">
                        <ToggleButton
                            value={this.state.withComments}
                            onChange={this.setWithComments}
                            name="withComments"
                        ></ToggleButton>
                    </div>
                    <div className="flex items-center gap-2  ">
                        <div>{translate('Find all transits with comments')}</div>
                    </div>
                </div>
            </div>
        );
    }
}

const TagsAndCommentsFiltersStateToProps = (state: RootState) => ({
    tags: state.port.tags,
    system: state.port.system,
    defaultData: state.port.defaultData,
    openModelFunction: state.layout.openModelFunction,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const TagsAndCommentsFiltersDispatchToProps = () => ({});

export default connect(
    TagsAndCommentsFiltersStateToProps,
    TagsAndCommentsFiltersDispatchToProps
)(TagsAndCommentsFilters);
